import { useLayoutEffect, useState, type RefObject } from 'react';

export type EntityType = 'spell' | 'item' | 'feature' | 'feat';

interface UseAutoScaleFontOptions {
  cardRef: RefObject<HTMLDivElement | null>;
  descriptionRef: RefObject<HTMLDivElement | null>;
  entityId: string;
  entityType: EntityType;
  /** Minimum font size percentage (default: 80 for spell/item, 70 for feature) */
  minFontSize?: number;
  /** Dependencies that should trigger recalculation */
  deps: unknown[];
}

/**
 * Hook that automatically scales font size to fit content within a card.
 * 
 * Uses CSS custom properties to share calculated scale between preview and print cards.
 * The scale is stored as `--{entityType}-{entityId}-scale` on document.documentElement.
 * 
 * @returns Current font size as a percentage (100 = 100%)
 */
export function useAutoScaleFont({
  cardRef,
  descriptionRef,
  entityId,
  entityType,
  minFontSize,
  deps,
}: UseAutoScaleFontOptions): number {
  const [fontSize, setFontSize] = useState(100);
  
  // Default min size: 70 for features (longer text), 80 for others
  const effectiveMinSize = minFontSize ?? (entityType === 'feature' ? 70 : 80);
  const cssVarName = `--${entityType}-${entityId}-scale`;

  useLayoutEffect(() => {
    if (!cardRef.current || !descriptionRef.current) return;

    const card = cardRef.current;
    const description = descriptionRef.current;
    
    // Check if card is visible
    const isVisible = card.offsetParent !== null;
    
    // Skip if card is not visible (e.g., display: none on print container)
    if (!isVisible) {
      // Try to read the pre-calculated value from CSS variable
      const checkForScale = (attempt = 1) => {
        const existingScale = getComputedStyle(document.documentElement).getPropertyValue(cssVarName);
        
        if (existingScale) {
          const scale = parseFloat(existingScale) * 100;
          setFontSize(scale);
        } else if (attempt < 5) {
          // Preview card might not have calculated yet, retry after a delay
          setTimeout(() => checkForScale(attempt + 1), 100);
        }
      };
      
      checkForScale();
      return;
    }
    
    const measureAndScale = () => {
      // Temporarily remove overflow:hidden to get accurate measurements
      const originalOverflow = description.style.overflow;
      description.style.overflow = 'visible';
      
      // Reset font size to 100% for measurement
      const originalFontSize = description.style.fontSize;
      description.style.fontSize = '100%';
      
      // Force reflow
      void description.offsetHeight;
      
      // Measure the description container's available height vs its content
      const availableHeight = description.clientHeight;
      let contentHeight = description.scrollHeight;
      
      // Skip measurement if dimensions aren't ready yet
      if (availableHeight === 0 || contentHeight === 0) {
        description.style.overflow = originalOverflow;
        description.style.fontSize = originalFontSize;
        return;
      }
      
      // If content fits at 100%, use that
      if (contentHeight <= availableHeight) {
        description.style.overflow = originalOverflow;
        description.style.fontSize = originalFontSize;
        document.documentElement.style.setProperty(cssVarName, '1');
        setFontSize(100);
        return;
      }

      // Find the right font size by testing progressively smaller sizes
      let bestSize = effectiveMinSize;
      
      // Test sizes from 98% down to minimum in 2% increments
      for (let size = 98; size >= effectiveMinSize; size -= 2) {
        description.style.fontSize = `${size}%`;
        
        // Force a reflow to get accurate measurement
        void description.offsetHeight;
        contentHeight = description.scrollHeight;
        
        if (contentHeight <= availableHeight) {
          bestSize = size;
          break;
        }
      }
      
      // Restore overflow and font size
      description.style.overflow = originalOverflow;
      description.style.fontSize = originalFontSize;
      
      // Store the calculated size globally so print cards can use it
      const scaleFactor = bestSize / 100;
      document.documentElement.style.setProperty(cssVarName, scaleFactor.toString());
      
      setFontSize(bestSize);
    };

    const timeoutId = setTimeout(measureAndScale, 0);
    
    return () => {
      clearTimeout(timeoutId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType, cssVarName, effectiveMinSize, ...deps]);

  return fontSize;
}
